import type { Vec3 } from "@models/data/entityData";
import type { SelectionSetPose } from "@models/data/selectionSetTransform";

/**
 * Renderer-side geometry seam for Unity's vertex-accurate floor placement.
 * The headless model owns persistence; the viewport owns rendered bounds and
 * converts the world-space drop back into snapshot-local coordinates.
 */
export interface IPlacementGeometryHost {
  calculateGroundedPosition(sceneId: string, snapshotId: string): Vec3 | null;
}

/**
 * Viewport-side seam for moving a selection SET in world space — implemented
 * by `TransformGizmoController`, the only thing holding the members' rendered
 * frames. Unity's wrapper carries a set edit to its members in world space
 * (`MultiExposeToEditor.cs:222-286`); a PART's stored transform is relative to
 * its parent, so applying the set's delta to stored transforms is only right
 * for root members.
 */
export interface ISelectionSetTransformHost {
  /**
   * Move the members so the set's pose becomes `pose`, then persist the
   * members and the set row. `null` when the host cannot do it (no member is
   * rendered); the caller then falls back.
   */
  transformSelectionSet(
    sceneId: string,
    selectionSetId: string,
    memberIds: Iterable<string>,
    pose: SelectionSetPose,
  ): Promise<void> | null;
}

export interface IPlacementModel {
  attachGeometryHost(host: IPlacementGeometryHost | null): void;
  attachSelectionSetTransformHost(host: ISelectionSetTransformHost | null): void;
  /** The registered host's world-space set move, or `null` when there is none. */
  tryTransformSelectionSet(
    sceneId: string,
    selectionSetId: string,
    memberIds: Iterable<string>,
    pose: SelectionSetPose,
  ): Promise<void> | null;
  placeOnGroundAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetPositionAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetRotationAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetTransformAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetScaleAsync(sceneId: string, snapshotId: string): Promise<void>;
  alignToCadOriginsAsync(sceneId: string, snapshotId: string): Promise<void>;
}

export const PLACEMENT_MODEL = Symbol.for("Campfire.PlacementModel");
